import React from "react";
import { useParams } from "react-router-dom";

// components
import SearchAndBrowsePageContainer from "core/SearchAndBrowsePageContainer";
import SearchAndBrowsePageHeadlineWithAvatar from "core/SearchAndBrowsePageHeadlineWithAvatar";
import SearchAndBrowseIconGridContainer from "icons/SearchAndBrowseIconGridContainer";
import colors from "core/colors";
import Edit from "core/ReusableIcons/Edit";
import NotFound from "core/NotFound";
import Link from "core/Link";

// queries
import { GET_ICONS_IN_COLLECTION } from "icons/BrowseIconsInCollection/queries";

// helpers
import useLoadMore from "frontend/hooks/useLoadMore";
import { useCurrentUserContext } from "contexts/CurrentUserContext";
import { PUBLIC_PROFILE } from "data/links";

// styles
import { HeadlineWithFort, IconBG } from "./styles";
import PageMeta from "core/PageMeta";

const BrowseIconsInCollection = () => {
    const limit = 200;
    const { collectionSlugWithId } = useParams();
    const collectionId = collectionSlugWithId.split("-").pop();

    const {
        items,
        totalCount,
        loadingFirstBatch,
        loading,
        LoadMore,
        loadingItemCount,
        data
    } = useLoadMore({
        limit: limit,
        query: GET_ICONS_IN_COLLECTION,
        queryVariables: {
            collectionId,
            limit
        },
        pathToListKey: "collectionDetail.icons.items",
        pathToTotalCountKey: "collectionDetail.icons.totalCount",
        contentType: "icon"
    });

    const showNotFound = data && !loading && !data.collectionDetail;

    if (showNotFound) return <NotFound />;

    const avatarUrl =
        data && data.collectionDetail && data.collectionDetail.creator.avatarUrl
            ? data.collectionDetail.creator.avatarUrl
            : "";
    const creatorName =
        data &&
        data.collectionDetail &&
        data.collectionDetail.creator &&
        data.collectionDetail.creator.name
            ? data.collectionDetail.creator.name
            : "";
    const username =
        data &&
        data.collectionDetail &&
        data.collectionDetail.creator &&
        data.collectionDetail.creator.username
            ? data.collectionDetail.creator.username
            : "";
    const collectionName =
        data && data.collectionDetail && data.collectionDetail.name
            ? data.collectionDetail.name
            : "";

    const {
        currentUser: { data: userData }
    } = useCurrentUserContext();
    const { currentUser } = userData;

    // NOTE:
    // These values also exist in templates/frontend/browse_icon_collections.html
    const pageMetaTitle = `${collectionName} Icon Collection by ${creatorName} | Noun Project`;
    const pageMetaDescription = `Explore the ${collectionName} icon collection by ${creatorName} at Noun Project. Browse these icons and millions more today!`;

    return (
        <>
            <PageMeta
                title={pageMetaTitle}
                description={pageMetaDescription}
                indexContent="index,follow,max-image-preview:large"
            />
            <SearchAndBrowsePageContainer>
                <HeadlineWithFort>
                    <SearchAndBrowsePageHeadlineWithAvatar
                        showSkeleton={loadingFirstBatch}
                        avatarUrl={avatarUrl}
                    >
                        {collectionName} by{" "}
                        <Link
                            size="lg"
                            styleType="primary"
                            display="inline"
                            href={PUBLIC_PROFILE.replace(
                                ":username",
                                encodeURIComponent(username)
                            )}
                        >
                            {creatorName}
                        </Link>
                    </SearchAndBrowsePageHeadlineWithAvatar>

                    {currentUser.isSuperuser && (
                        <a
                            href={`/fort/collections/${collectionId}/`}
                            target="_blank"
                        >
                            <IconBG>
                                <Edit
                                    fillColor={colors.primary.diamond}
                                    size="xs"
                                />
                            </IconBG>
                        </a>
                    )}
                </HeadlineWithFort>

                <SearchAndBrowseIconGridContainer
                    iconData={items}
                    totalCount={totalCount}
                    loadingFirstBatch={loadingFirstBatch}
                    loadingMore={loading}
                    LoadMoreButton={LoadMore}
                    loadingItemCount={loadingItemCount}
                />
            </SearchAndBrowsePageContainer>
        </>
    );
};

export default BrowseIconsInCollection;
